import React from "react";
import PropTypes from "prop-types";
import FuturePopup from "./FuturePopup";
import DemographicsForm from "./DemographicsForm";
import getToken from './getToken';
import _ from 'lodash';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faQuestion } from "@fortawesome/free-solid-svg-icons"


const THEMES = {
  home: '',
  work: '',
  nature: '',
  equality: '',
  everything_else: '',
}


const EXPLANATIONS = {
  home: 'For example, renting, buying or building? Just for now, or forever? In what type of community? How will availability of land affect these choices?',
  work: 'Do you aspire to work on the land in some way E.g. farming, forestry, outdoor access / recreation? How might access to land affect your ability to start a business or to find a job in the area you want to live in? Will changes to the way land is used affect your career aspirations?',
  nature: 'For example, how do we ensure nature can flourish? How can you access nature? How can nature have wider benefits to life? How could land be used to support climate action?',
  equality: 'For example, how can you play a part in making decisions about the way land around you is used? How can we all benefit from the ownership of land and buildings? How might the power to make decisions about land affect equality in your community?',
  everything_else: 'If you have an idea, but not sure where it fits this is the place for it!',
}


const EXAMPLES = [
  "Where and what your home is?",
  "The food we can produce?",
  "How you can travel?",
  "Jobs that are available?",
  "Where businesses can grow?",
  "How much CO2 can be captured?",
  "Where wildlife can live?",
  "The shape of your community?",
  "How we generate energy for homes and industry?",
  "Where you can go to get fresh air, peace and quiet, or just enjoy the view..."
]


const USES = [
  "Where and what your home is",
  "The food we can produce",
  "How you travel",
  "The jobs that are available",
  "Where businesses can grow",
  "How much CO2 can be captured",
  "Where wildlife can live",
  "The shape of your community",
  "How we generate energy",
  "Where you go for peace and quiet",
  "And a whole lot more...",
]


class Future extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selected: '',
      errors: null,
      aspirations: true,
      completed: false,
      use: USES[0],
      ...THEMES
    };
    this.timer;
    this.popupRef = React.createRef();
    this.aspirationsRef = React.createRef();
    this.demographicsRef = React.createRef();
    this.handleThemeClick = this.handleThemeClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSelectChange = this.handleSelectChange.bind(this);
    this.fadePopup = this.fadePopup.bind(this);
    this.handleFinishAspirations = this.handleFinishAspirations.bind(this);
    this.renderDemographics = this.renderDemographics.bind(this);
    this.submitSurvey = this.submitSurvey.bind(this);
    this.cycle = this.cycle.bind(this);
  }


  componentDidMount(){
    this.loadSurveyIntoState();
    setTimeout(() => this.toggleFade(this.aspirationsRef), 200);
    this.startCycling();
  }


  componentWillUnmount() {
    clearTimeout(this.timer);
  }


  startCycling(){
    this.timer = setInterval(this.cycle, 800)
  }


  stopCycling(){
    clearInterval(this.timer);
  }


  cycle(){
    USES.push(USES.shift());
    this.setState({use:USES[0]})
  }

  toggleFade(ref, callback = undefined){
    ref.current.classList.toggle('transparent');
    if(callback) callback();
  }


  loadSurveyIntoState(){
    const { survey } = this.props;
    if(survey){
      this.setState({
        home: survey.home,
        work: survey.work,
        nature: survey.nature,
        equality: survey.equality,
        everything_else: survey.everything_else,
      })
    }
  }


  handleThemeClick(theme){
    this.setState({ selected: theme }, () => this.focusTextArea(theme));
  }


  focusTextArea(theme){
    setTimeout(() => document.getElementById(`${theme}-textarea`).focus(), 500)
  }


  focusOn(id){
    document.getElementById(id).focus();
  }


  handleChange(e){
    const newState = this.state;
    newState[this.state.selected] = e.target.value;
    this.setState(newState);
  }


  handleSelectChange(e){
    const newState = this.state;
    newState[e.target.name] = e.target.value;
    this.setState(newState);
    this.focusNextSelect(e.target)
  }


  focusNextSelect(el){
    if(el.nextSibling){
      const focusable = el.nextSibling.nextSibling;
      if(focusable && focusable.nodeName === 'SELECT'){
        focusable.focus();
      }
    }
  }


  // Fade out Aspirations, wait till fade finishes, then scroll to Top
  // update state and fade in Demographics
  handleFinishAspirations(){
    if(this.props.survey){
      this.submitSurvey();
    } else {
      this.toggleFade(this.aspirationsRef);
      setTimeout(
        () => {
          window.scrollTo(0,0);
          this.setState({ aspirations:false }, this.renderDemographics)
        },
        700
      )
    }
  }


  renderDemographics(){
    document.getElementById(`select-year-of-birth`).focus()
    this.toggleFade(this.demographicsRef);
  }


  submitSurvey(e = undefined){
    if(e) e.preventDefault();

    const survey = {
      home: this.state.home,
      work: this.state.work,
      nature: this.state.nature,
      equality: this.state.equality,
      everything_else: this.state.everything_else,
      local_authority: this.state.local_authority,
      year_of_birth: this.state.year_of_birth,
      area_type: this.state.area_type,
    };

    let url = '/surveys';

    // Add ID to survey and URL if exists,
    if(this.props.survey) {
      url = url.concat(`/${this.props.survey.id}`);
      survey.id = this.props.survey.id;
    }

    fetch(url, {
      method: `${this.props.survey ? 'PATCH' : 'POST'}`,
      headers: {
        'X-CSRF-Token': getToken(),
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ survey: survey })
    })
      .then(res => res.json())
      .then(res => {
        // Render Errors - ISSUE - won't pick up the updated question list
        if(!res.success) this.setState({ errors: res.error });
        // Render Next Button and insert Survey ID into state
        else this.setState({ surveyId: res.data.id }, this.fadePopup);
      })
  }


  fadePopup(){
    this.popupRef.current.classList.add('show');
    this.popupRef.current.classList.toggle('accept-click');
  }


  setNextButtonLabel(){
    let hasText = false;
    Object.keys(THEMES).forEach(t => { if(this.state[t]) hasText = true });
    return hasText ? 'Next' : 'Skip'
  }


  toSentence(string){
    if(string.includes('_')){
      const split = string.split('_')
      return split.join(' ')
    } else {
      return string;
    }
  }


  render () {
    if(this.state.aspirations){

      // ACCORDION

      const accordion = Object.keys(THEMES).map((t, i) => (
        <li
          onClick={() => this.handleThemeClick(t)}
          className='accordion-item'
          data-accordion-item key={'accordion' + i}
        >

          <a
            href="#"
            className='accordion-title full-width align-justify'
          >

            <h3
              className='inline nm np'
            >
              {_.capitalize(this.toSentence(t))}
            </h3>

            <div
              className='flex-container align-middle'
            >
              {this.props.icon_paths[t] &&
                <img
                  src={this.props.icon_paths[t]}
                  alt={this.props.theme + ' logo'}
                  className='theme-icon'
                />
              }

              <FontAwesomeIcon
                icon={faCheck}
                color='white'
                // size='3rem'
                className={`nml aspiration-tick ${this.state[t] !== '' ? 'has-value' : ''}`}
              />

            </div>
          </a>


          <div className='accordion-content' data-tab-content>

            <p
              className='italic text-center mm lml lmr bold h6'
            >
              {EXPLANATIONS[t]}
            </p>

            <textarea
              id={`${t}-textarea`}
              className='nm sp'
              name={t}
              cols="30" rows="4"
              value={this.state[t]}
              onChange={this.handleChange}
              placeholder={`Please type your answer...`}
              // placeholder={`What are your aspirations regarding ${_.capitalize(this.toSentence(t))}?`}
            />
          </div>
        </li>
      ))

      // ASPIRATIONS ////////////////////////////////////////////////

      return (
        <React.Fragment>

          <div
            ref={this.aspirationsRef}
            id='aspirations'
            className='mpl mpr lpb flex-container align-center transparent grid-x'
          >
            <div className='cell medium-8'>

              {/*<h4
                className='text-center nmb italic'
                style={{letterSpacing: '0.06rem'}}
              >The Future of Scotland's Land</h4>*/}
              <h1
                className='text-center mmb'
              >
              Share your Aspirations for
              <br/>
              The Future of Scotland's Land
              </h1>


              <div className='white-bg dark-gray lp lmb'>

                <p className='text-center lead mmb'>
                  You maybe don’t think about it much, but the land beneath our feet affects so much of life in Scotland:
                </p>

                <div id='example-container' className='flex-container align-center-middle'>
                  <h3 className='text-center mmb bold italic' style={{color:'#00764b'}}>
                    {this.state.use.toUpperCase()}
                  </h3>
                </div>

                <p className='text-center lead mmb'>
                  In the long term, land decisions made today will affect young people the most. So we're asking young people to tell us what they hope for the future of Scotland’s land.
                </p>

                <p className='italic text-center bold'>
                  Tap below to SHARE YOUR ASPIRATIONS for Scotland’s land. To close, tap again or tap another heading. Write as much as you want.
                </p>

             </div>

              <ul className='accordion' data-accordion data-allow-all-closed>
                {accordion}
              </ul>

              <button
                className='button expanded '
                onClick={this.handleFinishAspirations}
              >
                <h3>
                  {this.setNextButtonLabel()}
                </h3>
              </button>

            </div>
          </div>


          <FuturePopup
            ref={this.popupRef}
            survey={this.props.survey}
            noQuiz={this.state.noQuiz}
            id={this.state.surveyId}
            handleNoQuiz={() => this.setState({noQuiz:true})}
          />

        </React.Fragment>
      );

    } else {

      // DEMOGRAPHICS //////////////////////////////////////////////////////
      return (

        <React.Fragment>

          <DemographicsForm
            ref={this.demographicsRef}
            handleSelectChange={this.handleSelectChange}
            submitSurvey={this.submitSurvey}
          />

          <FuturePopup
            ref={this.popupRef}
            survey={this.props.survey}
            noQuiz={this.state.noQuiz}
            id={this.state.surveyId}
            handleNoQuiz={() => this.setState({noQuiz:true})}
          />

        </React.Fragment>
      )
    } // END OF else BLOCK
  } // END OF RENDER
} // END OF CLASS

export default Future;
